import type { Paragraph, Table } from "docx";
import { downloadWordDocument } from "@/lib/documentGenerator/exportHistory";
import { createStampForCurrentUser } from "@/lib/documentGenerator/watermark";
import {
  body,
  buildDocument,
  fmtDate,
  h1,
  signatureBlock,
  table,
} from "@/lib/documentGenerator/docTheme";
import { USE_PHASE_STATUS_LABELS, type UsePhaseStatus } from "@/lib/basProcess/constants";
import { documentFooter } from "@/lib/documentGenerator/documentFooter";

export interface UsePhaseFollowupItem {
  section: string;
  control_point: string;
  legal_reference: string | null;
  status: string;
  responsible: string | null;
  checked_at: string | null;
  comment: string | null;
}

export interface UsePhaseFollowupDoc {
  space_name: string;
  description: string | null;
  status: string;
  items: UsePhaseFollowupItem[];
}

export const generateUsePhaseFollowupWordDocument = async (
  followup: UsePhaseFollowupDoc,
  projectName?: string
) => {
  const documentName = `Uppföljning bruksskede – ${followup.space_name}`;
  const stamp = await createStampForCurrentUser({
    docNumber: "33",
    documentName,
    projectName,
  });

  const children: (Paragraph | Table)[] = [];

  if (followup.description) {
    children.push(h1("Om utrymmet"), body(followup.description));
  }

  const sections = Array.from(new Set(followup.items.map((i) => i.section)));
  sections.forEach((s, index) => {
    children.push(
      h1(`${index + 1}. ${s}`),
      table(
        ["Kontrollpunkt", "Status", "Ansvarig", "Datum", "Kommentar"],
        followup.items
          .filter((i) => i.section === s)
          .map((i) => [
            i.legal_reference ? `${i.control_point}\n(${i.legal_reference})` : i.control_point,
            USE_PHASE_STATUS_LABELS[i.status as UsePhaseStatus] ?? i.status,
            i.responsible,
            i.checked_at ? fmtDate(i.checked_at) : "",
            i.comment,
          ]),
        { columnWidths: [3400, 1300, 1500, 1200, 1960] }
      )
    );
  });
  const footer = await documentFooter({ docNumber: "33", documentType: "uppfoljning_bruksskede" });


  const doc = buildDocument({
    meta: {
      version: footer.version,
      review: footer.review,
      docNumber: "33",
      documentName: "Uppföljning bruksskede – arbetsplatsens utformning",
      projectName,
      status: followup.status,
      extra: [["Utrymme", followup.space_name]],
    },
    stamp,
    children: [...children, ...footer.children],
  });

  await downloadWordDocument(
    doc,
    `Dok33_Uppfoljning_bruksskede_${followup.space_name.replace(/\s+/g, "_")}.docx`,
    {
      documentType: "uppfoljning_bruksskede",
      docNumber: "33",
      documentName,
      snapshot: followup,
      stamp,
    }
  );
};

export default generateUsePhaseFollowupWordDocument;
